import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

import ConfirmDialog from "../../common/ConfirmDialog";
import {
    getAppWebhook,
    updateAppWebhook,
    rotateAppWebhookSecret,
    getAppWebhookDeliveries,
} from "../../../redux/actions/AppWebhookActions";
import { APP_WEBHOOK_ROTATE_RESET } from "../../../redux/constants/AppWebhookConstants";

const trimBase = (url) => (url || "").replace(/\/+$/, "");

const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
};

const copyToClipboard = async (text, successMsg = "Copied.") => {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(successMsg);
    } catch (e) {
        toast.error("Couldn't copy to clipboard — select and copy manually.");
    }
};

const GiteaWebhookCard = ({ appId }) => {
    const dispatch = useDispatch();

    const appWebhook = useSelector((state) => state.appWebhook) || {};
    const { config, loading: configLoading } = appWebhook;

    const appWebhookUpdate = useSelector((state) => state.appWebhookUpdate) || {};
    const { loading: saveLoading } = appWebhookUpdate;

    const appWebhookRotate = useSelector((state) => state.appWebhookRotate) || {};
    const { loading: rotateLoading, plaintextSecret } = appWebhookRotate;

    const appWebhookDeliveries = useSelector((state) => state.appWebhookDeliveries) || {};
    const { deliveries = [], loading: deliveriesLoading } = appWebhookDeliveries;

    const [branch, setBranch] = useState("master");
    const [jenkinsJobName, setJenkinsJobName] = useState("");
    const [confirmRotateOpen, setConfirmRotateOpen] = useState(false);

    const apiBaseUrl = trimBase(process.env.REACT_APP_API_BASEURL);
    const webhookUrl = `${apiBaseUrl}/webhooks/gitea/${appId}`;

    useEffect(() => {
        if (appId) {
            dispatch(getAppWebhook(appId));
            dispatch(getAppWebhookDeliveries(appId));
        }
    }, [appId, dispatch]);

    useEffect(() => {
        if (config) {
            setBranch(config.branch || "master");
            setJenkinsJobName(config.jenkinsJobName || "");
        }
    }, [config]);

    const handleSave = () => {
        const trimmedBranch = branch.trim();
        if (!trimmedBranch) {
            toast.error("Branch is required.");
            return;
        }
        dispatch(updateAppWebhook(appId, {
            branch: trimmedBranch,
            jenkinsJobName: jenkinsJobName.trim(),
        }));
    };

    const handleRotateRequest = () => {
        if (config?.secretSet) {
            setConfirmRotateOpen(true);
        } else {
            dispatch(rotateAppWebhookSecret(appId));
        }
    };

    const handleConfirmRotate = () => {
        setConfirmRotateOpen(false);
        dispatch(rotateAppWebhookSecret(appId));
    };

    const closeRevealDialog = () => {
        dispatch({ type: APP_WEBHOOK_ROTATE_RESET });
    };

    const refreshDeliveries = () => {
        dispatch(getAppWebhookDeliveries(appId));
    };

    const hasReveal = Boolean(plaintextSecret?.secret);
    const secretSet = Boolean(config?.secretSet);

    return (
        <Box>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                sx={{ mb: 1.5 }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Gitea webhook (platform-triggered builds)
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {secretSet ? (
                    <Chip label="Configured" size="small" color="success" />
                ) : (
                    <Chip label="Not configured" size="small" />
                )}
            </Stack>

            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                Point a Gitea webhook at the URL below. On every push to the configured branch,
                the platform verifies the signature and triggers a Jenkins job on your behalf.
            </Typography>

            <Stack spacing={2}>
                <TextField
                    label="Webhook URL"
                    value={webhookUrl}
                    fullWidth
                    InputProps={{
                        readOnly: true,
                        sx: {
                            fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                            fontSize: 13,
                        },
                        endAdornment: (
                            <IconButton
                                edge="end"
                                size="small"
                                aria-label="copy webhook URL"
                                onClick={() => copyToClipboard(webhookUrl, "Webhook URL copied.")}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        ),
                    }}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                        label="Branch"
                        value={branch}
                        onChange={(e) => setBranch(e.target.value.slice(0, 100))}
                        sx={{ maxWidth: { sm: 220 } }}
                        helperText="Default: master"
                    />
                    <TextField
                        label="Jenkins job name"
                        value={jenkinsJobName}
                        onChange={(e) => setJenkinsJobName(e.target.value.slice(0, 200))}
                        fullWidth
                        helperText="Job to trigger on jenkins.barquecontech.com"
                    />
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saveLoading || configLoading}
                    >
                        {saveLoading ? "Saving…" : "Save"}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<VpnKeyIcon />}
                        onClick={handleRotateRequest}
                        disabled={rotateLoading || configLoading}
                    >
                        {secretSet ? "Rotate secret" : "Generate secret"}
                    </Button>
                    {secretSet && config?.secretPrefix && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                                fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                                fontSize: 13,
                            }}
                        >
                            current: bqt_whk_{config.secretPrefix}…
                        </Typography>
                    )}
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Recent deliveries
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton
                        size="small"
                        onClick={refreshDeliveries}
                        disabled={deliveriesLoading}
                        aria-label="refresh deliveries"
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Stack>

                {deliveries.length === 0 && !deliveriesLoading ? (
                    <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                        No deliveries yet.
                    </Typography>
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Time</TableCell>
                                    <TableCell>Event</TableCell>
                                    <TableCell>Ref</TableCell>
                                    <TableCell>Signature</TableCell>
                                    <TableCell>HTTP</TableCell>
                                    <TableCell>Jenkins status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {deliveries.map((d) => (
                                    <TableRow key={d.id}>
                                        <TableCell>{formatDate(d.attemptedAt)}</TableCell>
                                        <TableCell>{d.event || "—"}</TableCell>
                                        <TableCell
                                            sx={{
                                                fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                                                fontSize: 12,
                                            }}
                                        >
                                            {d.ref || "—"}
                                        </TableCell>
                                        <TableCell>
                                            {d.signatureValid ? (
                                                <Chip label="OK" size="small" color="success" />
                                            ) : (
                                                <Chip label="Invalid" size="small" color="error" />
                                            )}
                                        </TableCell>
                                        <TableCell>{d.responseCode ?? "—"}</TableCell>
                                        <TableCell
                                            sx={{
                                                fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                                                fontSize: 12,
                                            }}
                                        >
                                            {d.jenkinsStatus || "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Stack>

            <ConfirmDialog
                open={confirmRotateOpen}
                title="Rotate webhook secret?"
                body="This will invalidate the existing webhook secret. Your Gitea webhook will start failing until you update it with the new secret."
                confirmLabel="Rotate"
                confirmColor="error"
                onCancel={() => setConfirmRotateOpen(false)}
                onConfirm={handleConfirmRotate}
                loading={rotateLoading}
            />

            <Dialog open={hasReveal} onClose={closeRevealDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Webhook secret generated</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This secret is shown only once. Copy it now and paste it into the
                        secret field of your Gitea webhook before closing this dialog.
                    </Alert>
                    <TextField
                        fullWidth
                        value={plaintextSecret?.secret || ""}
                        InputProps={{
                            readOnly: true,
                            sx: {
                                fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                                fontSize: 13,
                            },
                            endAdornment: (
                                <IconButton
                                    edge="end"
                                    size="small"
                                    aria-label="copy secret"
                                    onClick={() =>
                                        copyToClipboard(
                                            plaintextSecret?.secret || "",
                                            "Secret copied.",
                                        )
                                    }
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            ),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button variant="contained" onClick={closeRevealDialog}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GiteaWebhookCard;
